import { useEffect, useMemo } from 'react';
import { Layer, Source, useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { addToken } from '~/api/layers2';
import colors, { PROJECT_AREA_FILL_COLOR, PROJECT_AREA_LINE_COLOR } from '~/colors';
import layerStore, { type MapboxVectorSourceConfig } from '~/store2/layers';

const CG_PROJECT_BOUNDARIES_LAYER_ID = 'civilgridProjectBoundaries';
const CG_PROJECT_CLUSTERS_LAYER_ID = 'civilgridProjectClusters';

const CLUSTER_ZOOM = 11;

const PROJECT_LINE_WIDTH = 4;
const UNDERLAY_PROJECT_LINE_WIDTH = 3;

function formatTileUrl(url: string, organizationId: string) {
  const withToken = addToken(url);
  const _url = new URL(withToken);
  _url.searchParams.append('organizationId', organizationId);
  return decodeURI(_url.toString());
}

export type ProjectClickEvent = mapboxgl.GeoJSONFeature & { lngLat: mapboxgl.MapMouseEvent['lngLat'] };

export default function ProjectsLayer({
  organizationId,
  onProjectClick,
  showClusters = true,
  isUnderlay = false,
}: {
  organizationId: string;
  showClusters?: boolean;
  onProjectClick?: (feature: ProjectClickEvent) => void;
  /** e.g. linked-org boundaries: 50% opacity, thinner dotted outline */
  isUnderlay?: boolean;
}) {
  const { current: map } = useMap();
  const { customLayersTilesConfig } = useSnapshot(layerStore) as typeof layerStore;

  const clusterLayerId = `cluster-${organizationId}`;
  const clusterCountLayerId = `cluster-count-${organizationId}`;

  useEffect(() => {
    const onClusterClick = (e: mapboxgl.MapMouseEvent) => {
      const currentMap = e.target;
      const features = currentMap.queryRenderedFeatures(e.point, {
        layers: [clusterLayerId],
      });

      if (features?.length) {
        const currentZoom = Math.round(currentMap.getZoom());
        const feature = features[0];

        if (feature?.geometry?.type === 'Point') {
          const [lng, lat] = feature.geometry.coordinates;
          let zoom = currentZoom + 1;

          if (feature.properties?.point_count === 1) {
            zoom = currentZoom + 2;
          }

          setTimeout(() => {
            currentMap.flyTo({
              center: [lng, lat],
              zoom,
            });
          }, 100);
        }
      }
    };

    const onPolygonClick = (e: mapboxgl.MapMouseEvent) => {
      const currentMap = e.target;
      const features = currentMap.queryRenderedFeatures(e.point, {
        layers: [`project-polygon-${organizationId}`],
      });

      if (features?.[0]?.geometry) {
        const feature = features[0];
        const event = { ...feature, lngLat: e.lngLat } satisfies ProjectClickEvent;
        onProjectClick?.(event);
      }
    };

    map?.on('click', clusterLayerId, onClusterClick);
    map?.on('click', `project-polygon-${organizationId}`, onPolygonClick);

    return () => {
      map?.off('click', clusterLayerId, onClusterClick);
      map?.off('click', `project-polygon-${organizationId}`, onPolygonClick);
    };
  }, [map, onProjectClick, organizationId, clusterLayerId]);

  const civilgridProjectBoundaries = useMemo(() => {
    if (!customLayersTilesConfig) return null;

    const config = customLayersTilesConfig[CG_PROJECT_BOUNDARIES_LAYER_ID] as MapboxVectorSourceConfig;
    if (!config) return null;

    const layerId = `${config.id}-${organizationId}`;

    return (
      <Source
        key={layerId}
        id={layerId}
        type="vector"
        tiles={config.tiles.map((url) => formatTileUrl(url, organizationId))}
        minzoom={CLUSTER_ZOOM + 1}
      >
        <Layer
          type="line"
          source={layerId}
          source-layer="all"
          layout={
            isUnderlay ? { 'line-cap': 'round', 'line-join': 'round' } : { 'line-cap': 'butt', 'line-join': 'round' }
          }
          paint={{
            'line-color': isUnderlay ? colors.blue50 : PROJECT_AREA_LINE_COLOR,
            'line-width': isUnderlay ? UNDERLAY_PROJECT_LINE_WIDTH : PROJECT_LINE_WIDTH,
            // [0, gap] + round caps renders as dots along the path (Mapbox style spec)
            'line-dasharray': isUnderlay ? ([0, 2] as [number, number]) : ([3, 1] as [number, number]),
            'line-opacity': isUnderlay ? 0.75 : 1,
            'line-emissive-strength': 1,
          }}
        />

        <Layer
          id={`project-polygon-${organizationId}`}
          type="fill"
          paint={{
            'fill-color': isUnderlay ? colors.blue50 : PROJECT_AREA_FILL_COLOR,
            'fill-opacity': isUnderlay ? 0.0375 : 0.05,
            'fill-emissive-strength': 1,
          }}
          source={layerId}
          source-layer="all"
        />
      </Source>
    );
  }, [customLayersTilesConfig, organizationId, isUnderlay]);

  const civilgridProjectClusters = useMemo(() => {
    if (!showClusters) return null;
    if (!customLayersTilesConfig) return null;

    const config = customLayersTilesConfig[CG_PROJECT_CLUSTERS_LAYER_ID] as MapboxVectorSourceConfig;
    if (!config) return null;

    const layerId = `${config.id}-${organizationId}`;

    return (
      <Source
        key={layerId}
        id={layerId}
        type="vector"
        tiles={config.tiles.map((url) => formatTileUrl(url, organizationId))}
      >
        <Layer
          id={clusterLayerId}
          type="circle"
          source={layerId}
          source-layer="all"
          filter={['has', 'point_count']}
          maxzoom={CLUSTER_ZOOM + 1}
          paint={{
            'circle-color': 'white',
            'circle-stroke-color': colors.brand,
            'circle-stroke-width': 2.5,
            'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
            'circle-emissive-strength': 1,
          }}
        />

        <Layer
          id={clusterCountLayerId}
          type="symbol"
          source={layerId}
          source-layer="all"
          filter={['has', 'point_count']}
          maxzoom={CLUSTER_ZOOM + 1}
          layout={{
            'text-field': ['get', 'point_count_abbreviated'],
            'text-size': 14,
            'text-allow-overlap': true,
            'text-ignore-placement': true,
            'symbol-sort-key': ['get', 'y'],
          }}
        />
      </Source>
    );
  }, [customLayersTilesConfig, organizationId, showClusters, clusterLayerId, clusterCountLayerId]);

  return (
    <>
      {civilgridProjectBoundaries}
      {civilgridProjectClusters}
    </>
  );
}
